/**
 * Formatting and labels shared by the Meetings and Availability tabs.
 *
 * Every instant arrives from the backend as UTC and is displayed in an
 * explicit zone — the organizer's, unless an admin picks another one. Nothing
 * here guesses a zone from the browser.
 */

const LOCALES = { en: "en-GB", ar: "ar-EG" }

/** Every zone the browser knows, with a short common list as the fallback. */
export const timezoneOptions = () => {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone")
    }
  } catch {
    /* falls through to the short list */
  }
  return [
    "Asia/Riyadh",
    "Asia/Dubai",
    "Asia/Kuwait",
    "Asia/Qatar",
    "Africa/Cairo",
    "Europe/London",
    "Europe/Paris",
    "Europe/Istanbul",
    "America/New_York",
    "UTC",
  ]
}

/** "GMT+3" for a zone at a given instant, so an offset is never assumed. */
export const zoneOffsetLabel = (timezone, iso = new Date().toISOString()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date(iso))
    return parts.find((part) => part.type === "timeZoneName")?.value || ""
  } catch {
    return ""
  }
}

const formatter = (lang, timezone, options) =>
  new Intl.DateTimeFormat(LOCALES[lang] || LOCALES.en, { timeZone: timezone, ...options })

export const formatDate = (iso, timezone, lang = "en") => {
  if (!iso) return ""
  try {
    return formatter(lang, timezone, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

export const formatTime = (iso, timezone, lang = "en") => {
  if (!iso) return ""
  try {
    return formatter(lang, timezone, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

/** "14:00 – 14:30", kept LTR so it reads correctly inside Arabic text. */
export const formatTimeRange = (startIso, endIso, timezone, lang = "en") =>
  `${formatTime(startIso, timezone, lang)} – ${formatTime(endIso, timezone, lang)}`

/**
 * Format a plain calendar day ("2026-11-04") for display.
 *
 * Deliberately not routed through a zone: turning the key into an instant and
 * formatting that instant shifts the label by a day either side of the date
 * line (1 November reads as 2 November in Pacific/Auckland).
 */
export const formatCalendarDate = (dateKey, lang = "en") => {
  const [year, month, day] = String(dateKey || "").split("-").map(Number)
  if (!year || !month || !day) return dateKey || ""
  try {
    return new Intl.DateTimeFormat(LOCALES[lang] || LOCALES.en, {
      timeZone: "UTC",
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(Date.UTC(year, month - 1, day)))
  } catch {
    return dateKey
  }
}

/** The calendar day an instant falls on, in the given zone: "2026-11-04". */
export const zoneDateKey = (iso, timezone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(iso))
    const pick = (type) => parts.find((part) => part.type === type)?.value || ""
    return `${pick("year")}-${pick("month")}-${pick("day")}`
  } catch {
    return ""
  }
}

/** Meeting status → a tag colour and a translation key. */
export const BOOKING_STATUS_TAGS = {
  confirmed: { color: "success", key: "status_confirmed" },
  pending: { color: "processing", key: "status_pending" },
  needs_review: { color: "error", key: "status_needs_review" },
  cancellation_pending: { color: "warning", key: "status_cancellation_pending" },
  cancelled: { color: "default", key: "status_cancelled" },
  failed: { color: "error", key: "status_failed" },
  completed: { color: "default", key: "status_completed" },
}

/** Slot state → a tag colour and a translation key. */
export const SLOT_STATE_TAGS = {
  available: { color: "success", key: "slot_available" },
  busy: { color: "purple", key: "slot_busy" },
  booked: { color: "processing", key: "slot_booked" },
  booking_in_progress: { color: "processing", key: "slot_in_progress" },
  unpublished: { color: "default", key: "slot_unpublished" },
  past: { color: "default", key: "slot_past" },
  unknown: { color: "warning", key: "slot_unknown" },
}

/**
 * A confirmed meeting whose end has passed reads as "completed".
 * Purely a display classification — no job rewrites the stored status.
 */
export const displayStatus = (booking) => {
  if (!booking) return ""
  if (booking.status === "confirmed" && booking.end_at && new Date(booking.end_at) < new Date()) {
    return "completed"
  }
  return booking.status
}

export const canCancel = (booking) =>
  booking?.status === "confirmed" && booking?.end_at && new Date(booking.end_at) > new Date()

export const isUnresolved = (booking) =>
  ["pending", "needs_review", "cancellation_pending"].includes(booking?.status)

/** Error code → the translation key whose copy explains it to an admin. */
export const ERROR_MESSAGE_KEYS = {
  validation_error: "error_validation",
  invalid_timezone: "error_invalid_timezone",
  slot_unavailable: "error_slot_unavailable",
  slot_expired: "error_slot_expired",
  slot_changed: "error_slot_changed",
  slot_overlap: "error_slot_overlap",
  slot_busy: "error_slot_busy",
  slot_in_use: "error_slot_in_use",
  settings_changed: "error_settings_changed",
  request_conflict: "error_request_conflict",
  booking_pending: "error_booking_pending",
  booking_failed: "error_booking_failed",
  calendar_unavailable: "error_calendar_unavailable",
  calendar_reconnect_required: "error_calendar_reconnect",
  calendar_mismatch: "error_calendar_mismatch",
  cancellation_pending: "error_cancellation_pending",
  cancellation_failed: "error_cancellation_failed",
  rate_limited: "error_rate_limited",
  not_configured: "error_not_configured",
  slot_not_found: "error_slot_not_found",
  oauth_scope_insufficient: "error_oauth_scope",
  scheduling_disabled: "error_scheduling_disabled",
}

/** `{code, message, fieldErrors, conflict}` from the scheduling envelope. */
export const parseSchedulingError = (error) => {
  const data = error?.response?.data
  if (data && typeof data === "object" && data.code) {
    return {
      code: data.code,
      message: data.message || "",
      fieldErrors: data.field_errors || null,
      conflict: data.conflict || null,
    }
  }
  // No envelope: a timeout, a dropped connection, or something that is not
  // this feature answering. The code is what the dialogs act on.
  return { code: "unknown", message: "", fieldErrors: null, conflict: null }
}

export const errorText = (t, parsed, fallback) => {
  const key = ERROR_MESSAGE_KEYS[parsed?.code]
  return (key && t?.[key]) || parsed?.message || fallback
}

/**
 * The same thing straight from a caught request, for callers that only show
 * the message. Where the code or the field errors are acted on, parse first
 * and pass the result to `errorText`.
 */
export const errorTextFrom = (t, error, fallback) =>
  errorText(t, parseSchedulingError(error), fallback)
